import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAppToast } from "@/contexts/ToastContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { validateSession } from "@/services/auth.service";
import { UserOrganizationRole } from "@blinkbooks/types";
import { ROLE_LABELS } from "@/lib/permissions";
import { useIsMobile } from "@/hooks/use-mobile";
import apiClient from "@/utils/httpClient";
import Sidebar from "@/components/Sidebar";
import SyncStatusIndicator from "@/components/SyncStatusIndicator";
import { useSyncPolling } from "@/hooks/use-sync-polling";
import { getSyncStatus, startFullSync } from "@/services/sync.quickbooks.service";
import { initiateQuickBooksConnect } from "@/services/integration.service";
import AIMode from "@/components/sidebar-components/AIMode";
import IntegrationsTab from "@/components/sidebar-components/IntegrationsTab";
import TeamTab from "@/components/sidebar-components/TeamTab";
import Overview from "@/components/sidebar-components/Overview";
import SalesReceivables from "@/components/sidebar-components/SalesReceivables";
import ExpensesPayables from "@/components/sidebar-components/ExpensesPayables";
import Banking from "@/components/sidebar-components/Banking";
import Accounting from "@/components/sidebar-components/Accounting";
import FinancialControlPanel from "@/components/sidebar-components/FinancialControlPanel";
import AtRiskCash from "@/components/sidebar-components/AtRiskCash";
import ReportsAnalytics from "@/components/sidebar-components/ReportsAnalytics";
import CashflowForecast from "@/components/sidebar-components/CashflowForecast";
import ActivityTab from "@/components/sidebar-components/ActivityTab";
import Vendors from "@/components/sidebar-components/Vendors";
import { DateFilterKey, DATE_FILTER_OPTIONS } from "@/utils/dateFilters";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/enhanced-button";
import { Textarea } from "@/components/ui/textarea";
import { ChevronDown, Menu, MessageSquare, CheckCircle2, LogOut, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

const TAB_LABELS: Record<string, string> = {
  "ai-mode": "AI Mode",
  "financial-control": "Financial KPIs",
  "for-you": "For You",
  "sales-receivables": "Sales & Receivables",
  "expenses-payables": "Expenses & Payables",
  "vendors": "Vendors",
  "banking": "Banking & Cards",
  "accounting": "Financial Structure",
  "reports-analytics": "Reports",
  "cashflow-forecast": "Cashflow & Forecast",
  "activity": "Activity",
  "integrations": "Integrations",
  "team": "Team",
};
import { formatDistanceToNow, parseISO, format } from "date-fns";
import { logout } from "@/services/auth.service";
import posthog, { registerSession, unregisterSession, captureError } from "@/lib/posthog";
import { getReminders } from "@/services/reminder.service";
import { RemindersButton } from "@/components/RemindersButton";
import { ReminderDto } from "@blinkbooks/types";


const Dashboard = () => {
  const [sidebarPinned, setSidebarPinned] = useState(() => localStorage.getItem('sidebar-pinned') === 'true');
  const [sidebarExpanded, setSidebarExpanded] = useState(() => localStorage.getItem('sidebar-pinned') === 'true');
  const handlePinnedChange = useCallback((pinned: boolean) => {
    setSidebarPinned(pinned);
    localStorage.setItem('sidebar-pinned', String(pinned));
  }, []);

  const [reminders, setReminders] = useState<ReminderDto[]>([]);
  const [aiInitialMessage, setAiInitialMessage] = useState<string | undefined>(undefined);
  const [aiModeKey, setAiModeKey] = useState(0);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadActivityCount, setUnreadActivityCount] = useState(0);
  const [vendorFilter, setVendorFilter] = useState<string | null>(null);
  const [hasIntegration, setHasIntegration] = useState<boolean | null>(null);

  // Global date filter — persisted in localStorage, shared across all date-filtering screens
  const LS_PERIOD_KEY = "blinkbooks_fcp_period";
  const LS_CUSTOM_FROM_KEY = "blinkbooks_fcp_custom_from";
  const LS_CUSTOM_TO_KEY = "blinkbooks_fcp_custom_to";
  const [globalDateFilterKey, setGlobalDateFilterKey] = useState<DateFilterKey>(() => {
    try {
      const saved = localStorage.getItem(LS_PERIOD_KEY) as DateFilterKey;
      return DATE_FILTER_OPTIONS.some(o => o.key === saved) ? saved : "last-30-days";
    } catch { return "last-30-days"; }
  });
  const [globalDateCustomFrom, setGlobalDateCustomFrom] = useState<Date | undefined>(() => {
    try {
      const saved = localStorage.getItem(LS_CUSTOM_FROM_KEY);
      return saved ? new Date(saved) : undefined;
    } catch { return undefined; }
  });
  const [globalDateCustomTo, setGlobalDateCustomTo] = useState<Date | undefined>(() => {
    try {
      const saved = localStorage.getItem(LS_CUSTOM_TO_KEY);
      return saved ? new Date(saved) : undefined;
    } catch { return undefined; }
  });
  useEffect(() => {
    try { localStorage.setItem(LS_PERIOD_KEY, globalDateFilterKey); } catch { /* storage unavailable */ }
  }, [globalDateFilterKey]);
  useEffect(() => {
    try {
      if (globalDateCustomFrom) localStorage.setItem(LS_CUSTOM_FROM_KEY, globalDateCustomFrom.toISOString());
      else localStorage.removeItem(LS_CUSTOM_FROM_KEY);
    } catch { /* storage unavailable */ }
  }, [globalDateCustomFrom]);
  useEffect(() => {
    try {
      if (globalDateCustomTo) localStorage.setItem(LS_CUSTOM_TO_KEY, globalDateCustomTo.toISOString());
      else localStorage.removeItem(LS_CUSTOM_TO_KEY);
    } catch { /* storage unavailable */ }
  }, [globalDateCustomTo]);
  const [role, setRole] = useState<UserOrganizationRole | null>(null);
  const [integrationExpired, setIntegrationExpired] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "ai-mode";
  const sectionEnteredAtRef = useRef<number>(Date.now());
  const prevTabRef = useRef<string>(activeTab);

  // Track time spent per section
  useEffect(() => {
    const prev = prevTabRef.current;
    if (prev !== activeTab) {
      const duration_seconds = Math.round((Date.now() - sectionEnteredAtRef.current) / 1000);
      posthog.capture("section_exited", { section: prev, duration_seconds });
      posthog.capture("section_viewed", { section: activeTab });
      sectionEnteredAtRef.current = Date.now();
      prevTabRef.current = activeTab;
    }
  }, [activeTab]);

  // Confirm genuine session engagement (filters out accidental opens / background tabs)
  useEffect(() => {
    const timer = setTimeout(() => {
      posthog.capture("active_session_confirmed", { section: activeTab, duration_seconds: 30 });
    }, 30_000);
    return () => clearTimeout(timer);
  }, [activeTab]);

  // Capture session end on tab/window close
  useEffect(() => {
    const handleUnload = () => {
      const duration_seconds = Math.round((Date.now() - sectionEnteredAtRef.current) / 1000);
      posthog.capture("section_exited", { section: prevTabRef.current, duration_seconds });
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);
  const { showToast } = useAppToast();
  const isMobile = useIsMobile();
  const { activeOrganizationId, activeViewId, refreshOrganizations } = useOrganization();

  // Get user and company data from navigation state
  const [user, setUser] = useState<{ id: string; email: string; firstName: string; lastName: string; phoneNumber?: string } | null>(location.state?.user || null);
  const [company, setCompany] = useState<{ id: string; name: string } | null>(location.state?.company || null);

  // Sync status indicator
  const [showSyncIndicator, setShowSyncIndicator] = useState(false);
  const { currentStep, displayedProgress, isComplete, error: syncError, startPolling } = useSyncPolling("Syncing data...", "FULL");

  useEffect(() => {
    if (!hasIntegration) return;

    const syncJustCompleted = location.state?.syncJustCompleted === true;
    const loginSyncQueued = location.state?.loginSyncQueued === true;

    if (syncJustCompleted) {
      // Initial sync just finished — queue the full background sync and start tracking it
      startFullSync()
        .then(() => {
          posthog.capture("sync_queued");
          setShowSyncIndicator(true);
          startPolling().catch(() => {});
        })
        .catch(() => {});
      return;
    }

    if (loginSyncQueued) {
      // A full sync was queued on login — start polling immediately (SyncStatus is already in DB)
      setShowSyncIndicator(true);
      startPolling().catch(() => {});
      return;
    }

    // On regular dashboard load, check sync state
    getSyncStatus().then((status) => {
      if (status?.currentSync?.syncType === 'FULL') {
        // Already running — just attach the poller
        setShowSyncIndicator(true);
        startPolling().catch(() => {});
      } else if (!status?.currentSync && !status?.lastSync && !integrationExpired) {
        // No sync on record and integration is healthy — auto-trigger
        startFullSync()
          .then(() => {
            setShowSyncIndicator(true);
            startPolling().catch(() => {});
          })
          .catch(() => {});
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasIntegration]);

  useEffect(() => {
    if (!isComplete) return;
    posthog.capture("sync_completed");
    const timer = setTimeout(() => setShowSyncIndicator(false), 4000);
    return () => clearTimeout(timer);
  }, [isComplete]);

  const handleReconnect = useCallback(async () => {
    try {
      const authUrl = await initiateQuickBooksConnect();
      window.location.href = authUrl;
    } catch (error) {
      console.error("Failed to initiate QuickBooks connection:", error);
      showToast({
        type: "error",
        title: "Connection Error",
        message: "Failed to start QuickBooks connection. Please try again.",
        autoHide: false,
      });
    }
  }, [showToast]);

  useEffect(() => {
    if (!syncError) return;
    posthog.capture("sync_failed", { error: syncError });
    captureError(new Error(syncError), { context: 'sync' });
    // Re-check integration status — the token may have been invalidated since mount
    validateSession().then((sessionData) => {
      const isAuthFailure = sessionData?.integrationStatus === 'error' || sessionData?.integrationStatus === 'expired';
      if (isAuthFailure) {
        setIntegrationExpired(true);
      }
      showToast({
        type: "error",
        title: "Sync Failed",
        message: syncError,
        autoHide: false,
        action: isAuthFailure ? { label: "Reconnect QuickBooks", onClick: handleReconnect } : undefined,
      });
    }).catch(() => {
      showToast({
        type: "error",
        title: "Sync Failed",
        message: syncError,
        autoHide: false,
      });
    });
  }, [syncError, showToast, handleReconnect]);

  // Fetch last successful sync time when integration is expired
  useEffect(() => {
    if (!integrationExpired) return;
    getSyncStatus().then((status) => {
      const last = status?.lastSync;
      if (last?.completedAt && (last.state === 'COMPLETED' || last.state === 'COMPLETED_WITH_ERRORS')) {
        setLastSyncAt(last.completedAt);
      }
    }).catch(() => {});
  }, [integrationExpired]);

  // Validate session on mount — always fetch to get role + hasIntegration
  useEffect(() => {
    const checkAuth = async () => {
      const sessionData = await validateSession();
      if (sessionData) {
        if (sessionData.integrationStatus === 'error' || sessionData.integrationStatus === 'expired') {
          if (sessionData.hasIntegration) {
            // Expired/errored connection — stay on dashboard and show reconnect warning
            setUser(sessionData.user);
            if (sessionData.organization) setCompany(sessionData.organization);
            setHasIntegration(sessionData.hasIntegration ?? false);
            setRole(sessionData.role ?? null);
            setIntegrationExpired(true);
            return;
          }
          navigate('/onboarding/connect', { replace: true });
          return;
        }
        setUser(sessionData.user);
        if (sessionData.organization) {
          setCompany(sessionData.organization);
        }
        setHasIntegration(sessionData.hasIntegration ?? false);
        setRole(sessionData.role ?? null);
        if (sessionData.user) {
          posthog.identify(sessionData.user.id, {
            email: sessionData.user.email,
            firstName: sessionData.user.firstName,
            lastName: sessionData.user.lastName,
            role: sessionData.role ?? undefined,
          });
          registerSession();
          posthog.capture("app_opened");
          posthog.capture("section_viewed", { section: "dashboard" });
          getReminders().then(setReminders).catch(() => {});
        }
      } else {
        navigate('/', { replace: true });
      }
    };

    checkAuth();
  }, [navigate]);

  // Re-fetch company/role/integration state when the active company or view is
  // switched (not on the initial mount — checkAuth above already handles that).
  const isFirstOrgRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstOrgRenderRef.current) {
      isFirstOrgRenderRef.current = false;
      return;
    }
    refreshOrganizations().then((sessionData) => {
      if (!sessionData) return;
      setUser(sessionData.user);
      if (sessionData.organization) setCompany(sessionData.organization);
      setHasIntegration(sessionData.hasIntegration ?? false);
      setRole(sessionData.role ?? null);
      // Keep the role person-property current — it can differ across organizations,
      // and most existing events aren't role-tagged individually, so this is what
      // makes retroactive role-based segmentation possible for them in PostHog.
      posthog.setPersonProperties({ role: sessionData.role ?? undefined });
    });
  }, [activeOrganizationId, activeViewId, refreshOrganizations]);

  // Handle QBO integration query params
  useEffect(() => {
    const integration = searchParams.get("integration");
    if (!integration) return;

    if (integration === "error") {
      const errorMessage = searchParams.get("message") || "Failed to connect QuickBooks. Please try again.";
      showToast({
        type: "error",
        title: "Integration Failed",
        message: errorMessage,
        autoHide: false,
      });
    }

    // Clear integration query params but preserve tab
    setSearchParams(
      (prev) => { const next = new URLSearchParams(prev); next.delete("integration"); next.delete("message"); return next; },
      { replace: true }
    );
  }, [searchParams, setSearchParams, showToast]);

  // No default unread count - all activities start as read
  useEffect(() => {
    setUnreadActivityCount(0);
  }, []);

  // Handle tab change and close mobile menu
  const handleTabChange = (tab: string) => {
    setSearchParams({ tab }, { replace: false });
    posthog.capture("dashboard_tab_changed", { tab });
    if (isMobile) {
      setMobileMenuOpen(false);
    }
    // Clear vendor filter when switching tabs
    if (tab !== 'expenses-payables') {
      setVendorFilter(null);
    }
  };

  // Handle vendor click from Vendors tab — always opens all-time view
  const handleVendorClick = (vendorName: string) => {
    setVendorFilter(vendorName);
    setGlobalDateFilterKey('all-time');
    setGlobalDateCustomFrom(undefined);
    setGlobalDateCustomTo(undefined);
    setSearchParams({ tab: 'expenses-payables' }, { replace: false });
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  // Handle feedback submission
  const handleFeedbackSubmit = async () => {
    try {
      await apiClient.post('/feedback', {
        message: feedbackText,
        selectedTab: activeTab,
      });

      posthog.capture("feedback_submitted", { active_tab: activeTab });
      // Show success state
      setFeedbackSubmitted(true);
      setFeedbackText("");

      // Close modal after 2 seconds
      setTimeout(() => {
        setFeedbackModalOpen(false);
        setFeedbackSubmitted(false);
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      captureError(error, { context: 'feedback_submit', active_tab: activeTab });
    }
  };

  const handleReminderRead = (id: string) => {
    setReminders(prev => prev.map(r => r.id === id ? { ...r, hasRead: true } : r));
  };

  const handleAskChat = (message: string) => {
    setAiInitialMessage(message);
    setAiModeKey(k => k + 1);
    setSearchParams({ tab: 'ai-mode' }, { replace: false });
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      posthog.capture("user_logged_out");
      await logout();
      unregisterSession();
      posthog.reset();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error during logout:', error);
      // Navigate to home even if logout fails
      navigate('/', { replace: true });
    }
  };



  const renderContent = () => {
    const dateProps = {
      dateFilterKey: globalDateFilterKey,
      dateCustomFrom: globalDateCustomFrom,
      dateCustomTo: globalDateCustomTo,
      onDateFilterChange: (key: DateFilterKey | '', from?: Date, to?: Date) => {
        const newKey = (key || 'last-30-days') as DateFilterKey;
        posthog.capture("date_filter_changed", { filter: newKey, section: activeTab, is_custom: newKey === 'custom' });
        setGlobalDateFilterKey(newKey);
        setGlobalDateCustomFrom(from);
        setGlobalDateCustomTo(to);
      },
    };
    switch (activeTab) {
      case "ai-mode":
        return <AIMode key={aiModeKey} initialMessage={aiInitialMessage} />;
      case "financial-control":
        return <FinancialControlPanel
          onNavigate={(tab) => handleTabChange(tab)}
          view="kpis"
          {...dateProps}
        />;
      case "for-you":
        return <FinancialControlPanel view="for-you" {...dateProps} />;
      case "at-risk-cash":
        return <AtRiskCash onBack={() => handleTabChange("financial-control")} />;
      case "overview":
        return <Overview />;
      case "sales-receivables":
        return <SalesReceivables onAskChat={handleAskChat} currentUserId={user?.id} role={role} {...dateProps} />;
      case "expenses-payables":
        return <ExpensesPayables vendorFilter={vendorFilter} onAskChat={handleAskChat} currentUserId={user?.id} role={role} {...dateProps} />;
      case "vendors":
        return <Vendors onVendorClick={handleVendorClick} />;
      case "banking":
        return <Banking {...dateProps} />;
      case "accounting":
        return <Accounting />;
      case "reports-analytics":
        return <ReportsAnalytics role={role} {...dateProps} />;
      case "cashflow-forecast":
        return <CashflowForecast {...dateProps} />;
      case "activity":
        return <ActivityTab onUnreadCountChange={setUnreadActivityCount} />;
      case "integrations":
        return <IntegrationsTab hasIntegration={hasIntegration === true} integrationError={integrationExpired} />;
      case "team":
        return <TeamTab role={role} currentUserId={user?.id} />;
      default:
        return <AIMode />;
    }
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop Sidebar */}
      {!isMobile && (
        <div className={cn(
          "flex-shrink-0 transition-all duration-300 h-full",
          sidebarExpanded ? "w-64" : "w-16"
        )}>
          <Sidebar
            activeTab={activeTab}
            onTabChange={handleTabChange}
            unreadActivityCount={unreadActivityCount}
            company={company}
            role={role}
            isPinned={sidebarPinned}
            onPinnedChange={handlePinnedChange}
            onExpandChange={setSidebarExpanded}
          />
        </div>
      )}

      {/* Mobile Sidebar Drawer */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-[280px]">
          <Sidebar
            activeTab={activeTab}
            onTabChange={handleTabChange}
            unreadActivityCount={unreadActivityCount}
            company={company}
            role={role}
          />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-14 md:h-16 border-b bg-card flex items-center justify-between px-3 md:px-6 shadow-sm flex-shrink-0">
          <div className="flex items-center gap-2 md:gap-4 min-w-0">
            {/* Mobile Menu Button */}
            {isMobile && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(true)}
                className="h-9 w-9 p-0 flex-shrink-0"
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}

            <div className="flex flex-col leading-none">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Command Center
              </span>
              <h1 className="text-lg font-semibold text-foreground leading-tight">
                {TAB_LABELS[activeTab] ?? "Dashboard"}
              </h1>
            </div>
          </div>

          {user && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {showSyncIndicator && (
                <SyncStatusIndicator
                  currentStep={currentStep}
                  displayedProgress={displayedProgress}
                  isComplete={isComplete}
                  error={syncError}
                />
              )}
              {integrationExpired && (!showSyncIndicator || !!syncError) && (
                <button
                  onClick={() => handleTabChange('integrations')}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                  aria-label={lastSyncAt
                    ? `Last sync: ${format(parseISO(lastSyncAt), 'MMM d, yyyy h:mm a')} (${formatDistanceToNow(parseISO(lastSyncAt), { addSuffix: true })}). Open integrations.`
                    : 'QuickBooks reconnect required. Open integrations.'}
                  title="Open integrations"
                >
                  <Clock className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                  <span className="hidden sm:inline">
                    {lastSyncAt
                      ? `Last sync: ${format(parseISO(lastSyncAt), 'MMM d, h:mm a')} (${formatDistanceToNow(parseISO(lastSyncAt), { addSuffix: true })})`
                      : 'Reconnect QuickBooks'}
                  </span>
                </button>
              )}
              <RemindersButton reminders={reminders} onReminderRead={handleReminderRead} />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFeedbackModalOpen(true)}
                className="h-8 w-8 p-0 hover:bg-blue-100"
                title="Send feedback"
              >
                <MessageSquare className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 h-9 md:h-10 px-2">
                    <div className="h-7 w-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-[11px] font-bold text-primary flex-shrink-0 uppercase">
                      {user.firstName[0]}{user.lastName?.[0] ?? ""}
                    </div>
                    <div className="hidden sm:flex flex-col items-start leading-none gap-0.5">
                      <span className="text-sm font-medium text-foreground">{user.firstName} {user.lastName}</span>
                      {role && (
                        <span className="text-[10px] font-medium tracking-wide text-muted-foreground">{ROLE_LABELS[role]}</span>
                      )}
                    </div>
                    <ChevronDown className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600 cursor-pointer">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </header>

        {/* Content Area */}
        <main key={activeViewId ?? activeOrganizationId} className="flex-1 overflow-auto">
          {renderContent()}
        </main>

      </div>

      {/* Feedback Modal */}
      <Dialog open={feedbackModalOpen} onOpenChange={(open) => {
        setFeedbackModalOpen(open);
        if (!open) {
          setFeedbackSubmitted(false);
          setFeedbackText("");
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {feedbackSubmitted ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Thank You!
                </>
              ) : (
                <>
                  <MessageSquare className="h-5 w-5 text-blue-600" />
                  Share Your Feedback
                </>
              )}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="pt-4 space-y-4">
                {feedbackSubmitted ? (
                  <div className="flex flex-col items-center justify-center py-8 space-y-4">
                    <CheckCircle2 className="h-20 w-20 text-green-600 animate-in zoom-in duration-300" />
                    <div className="text-lg font-medium text-foreground text-center">
                      Thank you for your feedback!
                    </div>
                    <div className="text-sm text-muted-foreground text-center">
                      We appreciate you taking the time to help us improve.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-base text-foreground">
                      What are you thinking about BlinkBooks so far?
                    </div>
                    <Textarea
                      placeholder="Share your thoughts, suggestions, or report any issues..."
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      className="min-h-[120px] resize-none"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setFeedbackModalOpen(false);
                          setFeedbackText("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleFeedbackSubmit}
                        disabled={!feedbackText.trim()}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Send Feedback
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;